import React, { useEffect, useMemo, useState } from "react";
import CustomHeader from "../../components/header/CustomHeader";
import {
  Button,
  Flex,
  Input,
  Table,
  DatePicker,
  Select,
  Dropdown,
  Tag,
} from "antd";
import { useToast } from "../../components/toast/toast";
import CustomModal from "../../components/modal/CustomModal";
import api from "../../api";
import { formatDate } from "../../utilities/utils";
import CustomInput from "../../components/input/CustomInput";
import CustomSelect from "../../components/select/CustomSelect";
import { IconArrowRight } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../store/context/AuthContext";
import { hasPerm } from "../../utilities/permission";

const { RangePicker } = DatePicker;

function CallCenter() {
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();

  // ---------- UI ----------
  const [showModal, setShowModal] = useState(false);

  // ---------- List ----------
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, pages: 1, limit: 10 });

  // ---------- Operators ----------
  const [operators, setOperators] = useState([]);
  const operatorMap = useMemo(
    () => Object.fromEntries((operators || []).map((o) => [o.id, o.name])),
    [operators],
  );

  // ---------- Filters ----------
  const [nameFilter, setNameFilter] = useState("");
  const [agentId, setAgentId] = useState(undefined);
  const [dateRange, setDateRange] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  // ---------- Create ----------
  const [name, setName] = useState("");
  const [selectedAgentId, setSelectedAgentId] = useState(undefined);
  const [newRange, setNewRange] = useState(null);
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [isCreate, setIsCreate] = useState(false);

  const handleChangePhones = (values) => {
    const cleaned = values.map((val) => val.trim());
    setPhoneNumbers(cleaned);
  };

  const resetCreateState = () => {
    setName("");
    setSelectedAgentId(undefined);
    setNewRange(null);
    setPhoneNumbers([]);
  };

  // ---------- Load operators once ----------
  useEffect(() => {
    (async () => {
      if (!hasPerm(user, "VIEW_ALL_CALL_LIST")) return;

      try {
        const res = await api.get_operator_list();
        setOperators(res?.data?.data || []);
      } catch {
        toast("Ажилчдын жагсаалт татахад алдаа гарлаа", "error");
      }
    })();
  }, [user]);

  // ---------- Fetch list ----------
  const fetchList = async () => {
    setLoading(true);
    const params = {
      name: nameFilter || "",
      agent_id: agentId || undefined,
      page,
      limit,
    };
    if (dateRange?.length === 2 && dateRange[0] && dateRange[1]) {
      params.date_from = dateRange[0].toISOString();
      params.date_to = dateRange[1].toISOString();
    }

    return api
      .get_call_list(params)
      .then((res) => {
        if (res?.status !== 200) return;

        const payload = res?.data?.data || {};
        const {
          total = 0,
          list = [],
          pageInt = 1,
          limitInt = 10,
          totalPages = 1,
        } = payload;

        setRows(list);
        setMeta({ total, page: pageInt, pages: totalPages, limit: limitInt });
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchList();
  }, [nameFilter, agentId, dateRange, page, limit]);

  // ---------- Table ----------
  const columns = useMemo(
    () => [
      { title: "Нэр", dataIndex: "name", key: "name", render: (v) => v || "-" },
      {
        title: "Утасны дугаар",
        key: "targets",
        render: (r) => r?._count?.Targets ?? 0,
      },
      {
        title: "Оролдлого",
        key: "attempts",
        render: (r) => r?._count?.Attempts ?? 0,
      },
      {
        title: "Хугацаа",
        key: "date_range",
        render: (_, r) => {
          const df = r?.date_from ? formatDate(r.date_from) : "-";
          const dt = r?.date_to ? formatDate(r.date_to) : "-";
          return `${df} → ${dt}`;
        },
      },
      {
        title: "Оператор",
        dataIndex: "agent_id",
        key: "agent_id",
        render: (v) => operatorMap[v] || "-",
      },
      //   {
      //     title: "Үүсгэсэн",
      //     dataIndex: "created_at",
      //     key: "created_at",
      //     render: (v) => (v ? formatDateTime(v) : "-"),
      //   },
      {
        title: "Төлөв",
        dataIndex: "status",
        key: "status",
        render: (status) =>
          (
            <Tag
              style={{
                borderStyle: "dashed",
                borderRadius: "6px",
                padding: "0 15px",
              }}
              color={
                status === "pending"
                  ? "orange"
                  : status === "in_progress"
                    ? "blue"
                    : status === "paused"
                      ? "gold"
                      : status === "done"
                        ? "green"
                        : status === "archived"
                          ? "default"
                          : "default"
              }
            >
              {status}
            </Tag>
          ) || "-",
      },
      {
        title: "Үйлдэл",
        key: "action",
        width: 150,
        render: (_, record) => {
          const items = (record) => [
            {
              key: "detail",
              label: (
                <span style={{ color: "black", fontSize: "13px" }}>
                  Дэлгэрэнгүй
                </span>
              ),
            },
          ];

          return (
            <Dropdown
              menu={{
                items: items(record),
                onClick: ({ key }) => {
                  if (key === "detail") {
                    navigate(`/call-center/${record.id}`);
                  }
                },
              }}
              trigger={["click"]}
            >
              <Button
                variant="filled"
                color="default"
                style={{ padding: "0 20px", fontSize: "12px" }}
              >
                Үйлдэл
                <IconArrowRight size={12} />
              </Button>
            </Dropdown>
          );
        },
      },
    ],
    [operatorMap],
  );

  const onChangePage = (p, pageSize) => {
    setPage(p);
    setLimit(pageSize);
  };

  // ---------- Create submit ----------
  const submitHandler = async () => {
    const payload = {
      name: name.trim(),
      agent_id: selectedAgentId,
      date_from: newRange?.[0]?.toISOString() || null,
      date_to: newRange?.[1]?.toISOString() || null,
      phoneNumbers,
    };

    setIsCreate(true);
    const res = await api.create_call_list(payload);

    // console.log("create res --->", res);
    if (res.status === 200) {
      toast("Дуудлагын жагсаалт нэмэгдлээ..", "success");
      setShowModal(false);
      resetCreateState();
      fetchList();
    } else {
      toast(res.data?.message || "Алдаа гарлаа.", "error");
    }
    setIsCreate(false);
  };

  return (
    <>
      <Flex vertical gap={10}>
        <CustomHeader
          title={"Дуудлагын жагсаалт"}
          rightSide={
            hasPerm(user, "CREATE_CALL_LIST") && (
              <Button
                variant="dashed"
                color="primary"
                onClick={() => setShowModal(true)}
                style={{ padding: "0px 20px" }}
              >
                + Дуудлага нэмэх
              </Button>
            )
          }
        />

        {/* Filters */}
        <Flex gap={10} wrap="wrap">
          <Input
            placeholder="Нэрээр хайх..."
            allowClear
            value={nameFilter}
            onChange={(e) => {
              setNameFilter(e.target.value);
              setPage(1);
            }}
            style={{ width: 240 }}
          />

          {hasPerm(user, "VIEW_ALL_CALL_LIST") && (
            <Select
              allowClear
              placeholder="Агент сонгох"
              value={agentId}
              onChange={(v) => {
                setAgentId(v);
                setPage(1);
              }}
              style={{ width: 220 }}
              options={operators.map((e) => ({ label: e.name, value: e.id }))}
            />
          )}

          <RangePicker
            value={dateRange}
            onChange={(v) => {
              setDateRange(v);
              setPage(1);
            }}
            style={{ minHeight: "32px" }}
          />
        </Flex>

        {/* Table */}
        <Table
          rowKey="id"
          dataSource={rows}
          columns={columns}
          loading={loading}
          pagination={{
            current: meta.page,
            pageSize: meta.limit,
            total: meta.total,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50, 100],
            onChange: onChangePage,
          }}
        />
      </Flex>

      {/* Create modal */}
      <CustomModal
        open={showModal}
        onOk={submitHandler}
        onCancel={() => {
          setShowModal(false);
          resetCreateState();
        }}
        title={"Дуудлагын жагсаалт нэмэх"}
        // disabled={isCreate}
        disabled={
          isCreate ||
          !name ||
          !selectedAgentId ||
          !newRange ||
          phoneNumbers.length === 0
        }
      >
        <Flex vertical gap={"10px"}>
          <CustomInput
            required
            title={"Гарчиг"}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />

          <CustomSelect
            required
            title={"Ажилтан сонгох"}
            options={operators.map((e) => ({ label: e.name, value: e.id }))}
            value={selectedAgentId}
            onChange={(val) => setSelectedAgentId(val)}
            disabled={operators.length === 0}
          />

          <Flex vertical gap={"2px"} style={{ width: "100%" }}>
            <p style={{ fontSize: "13px" }}>
              <span style={{ color: "#f00" }}>*</span>Эхлэх, дуусах огноо:
            </p>
            <RangePicker
              value={newRange}
              onChange={setNewRange}
              style={{ minHeight: "40px" }}
            />
          </Flex>

          <Flex vertical gap={"2px"} style={{ width: "100%" }}>
            <p style={{ fontSize: "13px" }}>
              <span style={{ color: "#f00" }}>*</span>Утасны дугаарууд: (
              {phoneNumbers.length || "0"})
            </p>
            <Select
              className="scrollable-tags-select"
              mode="tags"
              style={{ width: "100%" }}
              placeholder="Утасны дугаарууд оруулна уу"
              value={phoneNumbers}
              onChange={handleChangePhones}
              tokenSeparators={[",", " "]}
              suffixIcon={null}
              styles={{
                popup: {
                  root: { display: "none" },
                },
              }}
            />
          </Flex>
        </Flex>
      </CustomModal>
    </>
  );
}

export default CallCenter;
